import { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { isCommercialModeEnabled } from "@/utils/featureFlags";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, CheckCircle, Image as ImageIcon, LockKeyhole, Megaphone, X, XCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getPartnerFinance } from "@/data/financeStore";

const labels = {
  fr: {
    back: "Retour",
    pageTitle: "Proposer une publicité",
    pageDesc: "Diffusez un bandeau publicitaire pour promouvoir votre initiative.",
    restrictedTitle: "Accès restreint",
    restrictedDesc: "Seuls les espaces dédiés (Partis, ONG, Institutions, Médias et Candidats SAP) peuvent proposer des encarts publicitaires.",
    loginBtn: "Se connecter",
    blockedTitle: "Compte Bloqué",
    blockedDesc: "Votre compte est temporairement bloqué pour cause d'impayés. Vous ne pouvez plus proposer de nouvelles publicités tant que votre situation n'a pas été régularisée via votre tableau de bord.",
    regularizeBtn: "Régulariser ma situation",
    detailsTitle: "Détails de l'affichage",
    organizer: "Organisateur facturé :",
    titleLabel: "Titre de la campagne (Interne) *",
    titlePlaceholder: "Ex: Campagne de sensibilisation Sept. 2026",
    linkLabel: "Lien de redirection (Optionnel)",
    linkPlaceholder: "https://votre-site.ma/action",
    linkHelp: "L'utilisateur sera redirigé vers ce lien s'il clique sur la bannière.",
    imageLabel: "Bannière ou image promotionnelle *",
    clickToImport: "Cliquez",
    importText: " pour importer votre visuel",
    formatHelp: "Formats recommandés (Paysage) : PNG, JPG (MAX. 2Mo)",
    durationLabel: "Durée d'affichage",
    week1: "1 Semaine",
    days15: "15 Jours",
    month1: "1 Mois",
    locationLabel: "Lieu de diffusion",
    homeOnly: "Accueil Uniquement",
    dashboardsOnly: "Espaces Citoyens / Partis",
    global: "Toutes les pages (Global)",
    choose: "Choisir",
    geoSummaryTitle: "Résumé Géo-Tarifaire",
    geoSummaryDesc: "Coût calculé pour {duration} jours d'affichage (Merci de prendre contact avec le service commercial Kayn'in)",
    geoSummaryQuote: "\"C'est plutôt être solidaire!\"",
    charterText: "Je certifie que cette publicité respecte la charte éthique de la plateforme (pas de diffamation, discours de haine ou incitation à la violence).",
    submitBtn: "Soumettre la publicité pour validation",
    successTitle: "Demande soumise !",
    successDesc: "Votre publicité a été soumise avec succès. Elle sera activée après validation par l'équipe d'administration.",
    backToDash: "Retour au tableau de bord",
    errorTooBig: "L'image est trop grande (max 2Mo)."
  },
  ar: {
    back: "عودة",
    pageTitle: "اقتراح إعلان",
    pageDesc: "انشر لافتة إعلانية للترويج لمبادرتك.",
    restrictedTitle: "وصول مقيد",
    restrictedDesc: "فقط المساحات المخصصة (الأحزاب، المنظمات غير الحكومية، المؤسسات، وسائل الإعلام والمرشحون SAP) يمكنها اقتراح مساحات إعلانية.",
    loginBtn: "تسجيل الدخول",
    blockedTitle: "حساب محظور",
    blockedDesc: "حسابك محظور مؤقتًا بسبب عدم الدفع. لا يمكنك اقتراح إعلانات جديدة حتى تتم تسوية وضعيتك عبر لوحة التحكم الخاصة بك.",
    regularizeBtn: "تسوية وضعيتي",
    detailsTitle: "تفاصيل العرض",
    organizer: "المنظم المفوتر :",
    titleLabel: "عنوان الحملة (داخلي) *",
    titlePlaceholder: "مثال: حملة توعية شتنبر 2026",
    linkLabel: "رابط التوجيه (اختياري)",
    linkPlaceholder: "https://votre-site.ma/action",
    linkHelp: "سيتم توجيه المستخدم إلى هذا الرابط إذا نقر على اللافتة.",
    imageLabel: "لافتة أو صورة ترويجية *",
    clickToImport: "انقر",
    importText: " لاستيراد صورتك",
    formatHelp: "التنسيقات الموصى بها (أفقي) : PNG, JPG (الحد الأقصى 2 ميجابايت)",
    durationLabel: "مدة العرض",
    week1: "أسبوع واحد",
    days15: "15 يومًا",
    month1: "شهر واحد",
    locationLabel: "مكان النشر",
    homeOnly: "الاستقبال فقط",
    dashboardsOnly: "فضاءات المواطنين / الأحزاب",
    global: "جميع الصفحات (عام)",
    choose: "اختر",
    geoSummaryTitle: "ملخص التعرفة الجغرافية",
    geoSummaryDesc: "التكلفة المحسوبة لـ {duration} يومًا من العرض (يرجى الاتصال بالخدمة التجارية لـ Kayn'in)",
    geoSummaryQuote: "\"هذا بالأحرى تضامن!\"",
    charterText: "أشهد أن هذا الإعلان يحترم الميثاق الأخلاقي للمنصة (لا تشهير، ولا خطاب كراهية ولا تحريض على العنف).",
    submitBtn: "إرسال الإعلان للتحقق",
    successTitle: "تم تقديم الطلب !",
    successDesc: "تم تقديم إعلانك بنجاح. سيتم تفعيله بعد التحقق من قبل فريق الإدارة.",
    backToDash: "العودة إلى لوحة التحكم",
    errorTooBig: "الصورة كبيرة جدًا (الحد الأقصى 2 ميجابايت)."
  },
  zgh: {
    back: "ⴰⵖⵓⵍ",
    pageTitle: "ⵙⵎⵏⵉⴷ ⴰⵙⵓⵙⵙⵏ",
    pageDesc: "ⴼⵙⵔ ⵜⴰⴼⵉⵛⵜ ⵏ ⵓⵙⵓⵙⵙⵏ ⴰⴼⴰⴷ ⴰⴷ ⵜⵙⵓⵙⵙⵏⵜ ⵜⴰⵙⵏⵜⴰⵢⵜ ⵏⵏⴽ.",
    restrictedTitle: "ⴰⴽⵛⵛⵓⵎ ⵉⵜⵜⵓⴳⴷⴰⵍⵏ",
    restrictedDesc: "ⵖⴰⵔ ⵉⵙⴰⵢⵔⴰⵔⵏ ⵉⵥⵍⵉⵏ (ⵉⴽⴰⴱⴰⵔⵏ, ONG, ⵜⵉⵎⵔⵙⴰⵍ, ⵉⵎⵉⴷⵢⴰⵜⵏ ⴷ ⵉⵎⵙⵜⴰⵢⵏ SAP) ⵉⵖⵉⵢⵏ ⴰⴷ ⵙⵎⵏⵉⴷⵏ ⴰⵙⵓⵙⵙⵏ.",
    loginBtn: "ⴽⵛⵎ",
    blockedTitle: "ⴰⵎⵉⴹⴰⵏ ⵉⵜⵜⵓⵇⵇⵏⵏ",
    blockedDesc: "ⴰⵎⵉⴹⴰⵏ ⵏⵏⴽ ⵉⵜⵜⵓⵇⵇⵏ ⵙ ⵜⵎⵏⵜⵉⵍⵜ ⵏ ⵡⴰⵔ ⴰⵙⴼⵔⵓ. ⵓⵔ ⵜⵖⵉⵢⵜ ⴰⴷ ⵜⵙⵎⵏⵉⴷⵜ ⵉⵙⵓⵙⵙⵏⵏ ⵉⵎⴰⵢⵏⵓⵜⵏ ⴰⵔ ⴷ ⵜⵙⴳⴳⴷⵜ ⴰⴷⴷⴰⴷ ⵏⵏⴽ ⴳ ⵜⴼⵉⵍⵜ ⵏ ⵓⵙⵏⵇⴷ.",
    regularizeBtn: "ⵙⴳⴳⴷ ⴰⴷⴷⴰⴷ ⵉⵏⵓ",
    detailsTitle: "ⵉⵏⵖⵎⵉⵙⵏ ⵏ ⵓⴼⵙⴰⵔ",
    organizer: "ⴰⵎⵙⵓⴷⴷⵙ ⵉⵜⵜⵓⴼⴰⴽⵜⵓⵔⵏ :",
    titleLabel: "ⴰⵣⵡⵍ ⵏ ⵜⵎⵀⵍⵜ (ⴰⴳⵯⵏⵙⴰⵏ) *",
    titlePlaceholder: "ⴰⵎⴷⵢⴰ: ⵜⴰⵎⵀⵍⵜ ⵏ ⵓⵙⴼⵔⴽ ⵛⵓⵜⴰⵏⴱⵉⵔ 2026",
    linkLabel: "ⴰⵙⵖⵏ ⵏ ⵓⵙⵡⴰⵍⴰ (ⴰⵎⴰⵖⴷⴰⵢ)",
    linkPlaceholder: "https://votre-site.ma/action",
    linkHelp: "ⴰⵎⵙⵎⵔⵙ ⴰⴷ ⵉⴷⴷⵓ ⵖⵔ ⵓⵙⵖⵏ ⴰⴷ ⵎⴽ ⵉⴽⵍⵉⴽⴰ ⵅⴼ ⵜⴼⵉⵛⵜ.",
    imageLabel: "ⵜⴰⴼⵉⵛⵜ ⵏⵖ ⵜⴰⵡⵍⴰⴼⵜ ⵏ ⵓⵙⵓⵙⵙⵏ *",
    clickToImport: "ⴽⵍⵉⴽⵉ",
    importText: " ⴰⴼⴰⴷ ⴰⴷ ⵜⵙⵉⴷⴼⵜ ⵜⴰⵡⵍⴰⴼⵜ ⵏⵏⴽ",
    formatHelp: "ⵉⵎⴰⵙⵙⵏ ⵉⵜⵜⵓⵙⵎⵎⴰⵜⵔⵏ : PNG, JPG (ⴰⵎⴰⴽⵙⵉⵎⵓⵎ 2Mo)",
    durationLabel: "ⴰⴽⵓⴷ ⵏ ⵓⴼⵙⴰⵔ",
    week1: "1 ⵉⵎⴰⵍⴰⵙⵙ",
    days15: "15 ⵏ ⵡⵓⵙⵙⴰⵏ",
    month1: "1 ⴰⵢⵢⵓⵔ",
    locationLabel: "ⴰⵏⵙⴰ ⵏ ⵓⴼⵙⴰⵔ",
    homeOnly: "ⴰⵙⵏⵓⴱⴳ ⴽⴰ",
    dashboardsOnly: "ⵉⵙⴰⵢⵔⴰⵔⵏ ⵉⵏⴰⵎⵓⵔⵏ / ⵉⴽⴰⴱⴰⵔⵏ",
    global: "ⵎⴰⵕⵕⴰ ⵜⵉⵙⵏⵉⵡⵉⵏ (ⴰⵎⴰⵜⴰⵢ)",
    choose: "ⵙⵜⵉ",
    geoSummaryTitle: "ⴰⴳⵣⵓⵍ ⵏ ⵡⴰⵜⵉⴳ ⴰⵔⴰⴽⴰⵍ",
    geoSummaryDesc: "ⴰⵜⵉⴳ ⵉⵜⵜⵓⵙⵉⴹⵏ ⵉ {duration} ⵏ ⵡⵓⵙⵙⴰⵏ ⵏ ⵓⴼⵙⴰⵔ (ⵙⴰⵡⵍ ⴷ ⵜⴰⵏⴰⴼⵓⵜ ⵜⴰⵙⴱⴱⴰⴱⵜ ⵏ Kayn'in)",
    geoSummaryQuote: "\"ⴰⴷ ⵏⴳ ⵉⵎⵢⴰⵡⴰⵙⵏ ⵢⵓⴼ!\"",
    charterText: "ⴰⵔ ⵜⵜⵉⵏⵉⵖ ⵎⴰⵙⴷ ⴰⵙⵓⵙⵙⵏ ⴰⴷ ⵉⵇⴰⴷⴰⵔ ⴰⵎⵓⵍⵍⵉ ⵏ ⵓⵙⴰⵢⵔⴰⵔ (ⵓⵔ ⵉⵍⵍⵉ ⵓⵙⵔⴽⵙ, ⵓⵔ ⵉⵍⵍⵉ ⵢⵉⵏⴰⵡ ⵏ ⵓⴽⵕⵟ, ⵓⵔ ⵉⵍⵍⵉ ⵓⵙⵙⵉⵥⵥⵓ ⵅⴼ ⵜⴽⵕⴹⵉ).",
    submitBtn: "ⴰⵣⵏ ⴰⵙⵓⵙⵙⵏ ⴰⴼⴰⴷ ⴰⴷ ⵉⵜⵜⵓⵙⵖⵣⵏ",
    successTitle: "ⵜⵓⵜⵔⴰ ⵜⵜⵓⵢⴰⵣⵏ !",
    successDesc: "ⴰⵙⵓⵙⵙⵏ ⵏⵏⴽ ⵉⵜⵜⵓⵢⴰⵣⵏ ⵙ ⵓⵎⵓⵔⵙ. ⴰⴷ ⵉⵜⵜⵓⵙⵡⵓⵔⵉ ⴷⴼⴼⵉⵔ ⴰⵙⵖⵣⵏ ⵏ ⵜⵔⴰⴱⴱⵓⵜ ⵏ ⵜⵎⵙⵙⵓⴳⵓⵔⵜ.",
    backToDash: "ⴰⵖⵓⵍ ⵙ ⵜⴼⵉⵍⵜ ⵏ ⵓⵙⵏⵇⴷ",
    errorTooBig: "ⵜⴰⵡⵍⴰⴼⵜ ⵜⵎⵇⵇⵓⵔ ⴱⴰⵀⵔⴰ (ⴰⵎⴰⴽⵙⵉⵎⵓⵎ 2Mo)."
  }
};

const ProposerPublicitePage = () => {
  const { t, lang } = useI18n();
  const l = labels[lang as keyof typeof labels] || labels.fr;
  const navigate = useNavigate();
  const { isAuthenticated, user, isParty, isOng, isInstitution, isMedia } = useAuth();
  const isSAP = user?.partyAcronym === 'SAP';
  const canAccess = isAuthenticated && (isParty || isOng || isInstitution || isMedia || isSAP);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  
  const [submitted, setSubmitted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [commercialModeOn, setCommercialModeOn] = useState(isCommercialModeEnabled());

  useEffect(() => {
    const handleModeChange = (e: any) => setCommercialModeOn(e.detail.enabled);
    window.addEventListener("commercialModeChanged", handleModeChange);
    return () => window.removeEventListener("commercialModeChanged", handleModeChange);
  }, []);

  const [formData, setFormData] = useState({
    title: "",
    linkUrl: "",
    imageUrl: "",
    duration: "7",
    location: "homepage"
  });

  const partnerFinance = user && user.role !== 'CITIZEN' 
    ? getPartnerFinance(user.partyAcronym || user.name || "ONG")
    : null;
  const isBlocked = commercialModeOn && partnerFinance?.isBlocked;

  const cost = formData.duration === "7" ? 150 : formData.duration === "15" ? 250 : 400;

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container pt-24 pb-16 flex items-center justify-center min-h-[80vh]">
          <Card className="max-w-md w-full text-center shadow-elevated">
            <CardContent className="p-8">
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
              <h2 className="font-display font-bold text-2xl text-foreground mb-3">{l.successTitle}</h2>
              <p className="text-muted-foreground font-body mb-6">
                {l.successDesc}
              </p>
              <Button onClick={() => user ? navigate('/dashboard') : navigate('/')}>{l.backToDash}</Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newAd = {
      id: `ad_${Date.now()}`,
      title: formData.title,
      linkUrl: formData.linkUrl,
      imageUrl: formData.imageUrl,
      duration: formData.duration,
      location: formData.location,
      cost: cost,
      organizerName: user?.ongName || user?.partyAcronym || user?.institutionName || user?.channelName || user?.name || 'Inconnu',
      organizerId: user?.id,
      submittedAt: new Date().toISOString(),
      status: "pending" as const,
      paymentStatus: "unpaid" as const
    };

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const API_URL = import.meta.env.VITE_API_URL || "https://kaynin.ma/backend/api";
      const res = await fetch(`${API_URL}/ads.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action: "save_partner_ad", ad: newAd })
      });
      if (!res.ok) throw new Error("API Error");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Erreur réseau");
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert(l.errorTooBig);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, imageUrl: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container pt-24 pb-16 max-w-2xl">
        <Button variant="ghost" className="mb-6 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180" />{l.back}
        </Button>

        <div className="text-center mb-8">
          <Megaphone className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-display font-bold text-3xl text-foreground mb-2">{l.pageTitle}</h1>
          <p className="text-muted-foreground font-body">
            {l.pageDesc}
          </p>
        </div>

        {!canAccess ? (
          <div className="flex flex-col items-center justify-center p-12 bg-card rounded-2xl border border-destructive/20 shadow-md text-center max-w-lg mx-auto">
             <LockKeyhole className="w-16 h-16 text-muted-foreground mb-6 opacity-80" />
             <h3 className="text-2xl font-display font-bold text-foreground mb-3">
               {l.restrictedTitle}
             </h3>
             <p className="text-muted-foreground font-body mb-8">
               {l.restrictedDesc}
             </p>
             {!isAuthenticated && (
               <Button size="lg" onClick={() => setIsAuthModalOpen(true)} className="font-body font-bold w-full">
                 {l.loginBtn}
               </Button>
             )}
          </div>
        ) : isBlocked ? (
          <div className="flex flex-col items-center justify-center p-12 bg-red-50 rounded-2xl border border-red-200 shadow-md text-center max-w-lg mx-auto">
             <XCircle className="w-16 h-16 text-red-500 mb-6" />
             <h3 className="text-2xl font-display font-bold text-red-900 mb-3">{l.blockedTitle}</h3>
             <p className="text-red-700 font-body mb-8">
               {l.blockedDesc}
             </p>
             <Button size="lg" variant="destructive" asChild className="font-body font-bold w-full text-white">
               <Link to="/dashboard">{l.regularizeBtn}</Link>
             </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            <Card className="shadow-card">
              <CardHeader><CardTitle className="text-lg font-display">{l.detailsTitle}</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <div className="p-4 bg-primary/5 rounded-xl border border-primary/20 mb-4">
                  <p className="text-sm text-primary font-medium">
                    <strong>{l.organizer}</strong> {user?.ongName || user?.partyAcronym || user?.institutionName || user?.channelName || user?.name}
                  </p>
                </div>

                <div>
                  <Label className="font-body">{l.titleLabel}</Label>
                  <Input required className="mt-1" placeholder={l.titlePlaceholder} value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                </div>

                <div>
                  <Label className="font-body">{l.linkLabel}</Label>
                  <Input type="url" className="mt-1" placeholder={l.linkPlaceholder} value={formData.linkUrl} onChange={(e) => setFormData({...formData, linkUrl: e.target.value})} />
                  <p className="text-xs text-muted-foreground mt-1">{l.linkHelp}</p>
                </div>

                <div>
                  <Label className="font-body mb-2 block">{l.imageLabel}</Label>
                  {!formData.imageUrl ? (
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-primary/40 bg-primary/5 rounded-lg cursor-pointer hover:bg-primary/10 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6 text-muted-foreground">
                          <ImageIcon className="w-10 h-10 mb-3 opacity-70 text-primary" />
                          <p className="mb-2 text-sm font-body"><span className="font-semibold text-primary">{l.clickToImport}</span>{l.importText}</p>
                          <p className="text-xs">{l.formatHelp}</p>
                        </div>
                        <input type="file" required className="hidden" accept="image/*" onChange={handleImageUpload} />
                      </label>
                    </div>
                  ) : (
                    <div className="relative w-full rounded-lg overflow-hidden border border-border group">
                      <img src={formData.imageUrl} alt="Prévisualisation de la bannière" className="w-full h-auto object-cover max-h-48" />
                      <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2 w-8 h-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setFormData({ ...formData, imageUrl: "" })}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="font-body mb-2 block">{l.durationLabel}</Label>
                    <Select value={formData.duration} onValueChange={v => setFormData({ ...formData, duration: v })}>
                      <SelectTrigger><SelectValue placeholder={l.choose} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">{l.week1}</SelectItem>
                        <SelectItem value="15">{l.days15}</SelectItem>
                        <SelectItem value="30">{l.month1}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-body mb-2 block">{l.locationLabel}</Label>
                    <Select value={formData.location} onValueChange={v => setFormData({ ...formData, location: v })}>
                      <SelectTrigger><SelectValue placeholder={l.choose} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="homepage">{l.homeOnly}</SelectItem>
                        <SelectItem value="dashboard">{l.dashboardsOnly}</SelectItem>
                        <SelectItem value="global">{l.global}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {commercialModeOn && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h4 className="font-display font-bold text-foreground">{l.geoSummaryTitle}</h4>
                    <p className="text-sm text-muted-foreground font-body mt-1">
                      {l.geoSummaryDesc.replace('{duration}', formData.duration)}
                    </p>
                  </div>
                  <div className="md:text-right shrink-0">
                    <span className="italic text-primary font-medium">{l.geoSummaryQuote}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex items-start gap-3 px-1">
              <Checkbox id="declaration" required checked={accepted} onCheckedChange={(v) => setAccepted(v === true)} />
              <Label htmlFor="declaration" className="font-body text-sm text-muted-foreground leading-relaxed cursor-pointer">
                {l.charterText}
              </Label>
            </div>

            <Button type="submit" size="lg" className="w-full font-body font-semibold" disabled={!accepted || !formData.imageUrl || !formData.title}>
              {l.submitBtn}
            </Button>
          </form>
        )}
      </div>
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      <Footer />
    </div>
  );
};

export default ProposerPublicitePage;
